import React, { useState } from 'react';
import { Check, CheckCircle2, ChevronRight } from 'lucide-react';
import type { CompetencyDomain, FrameworkItem, ItemType, Role } from '../../types/framework';
import { ITEM_TYPE_LABEL, PICKS_PER_DOMAIN, PICKS_PER_TYPE, domainProgress, questionnaireSize } from '../../utils/framework';
type QuestionPickerProps = {
  role: Role;
  selected: number[];
  onToggle: (item: FrameworkItem, domain: CompetencyDomain) => void;
};
export function QuestionPicker({
  role,
  selected,
  onToggle
}: QuestionPickerProps) {
  const [activeId, setActiveId] = useState(role.domains[0]?.id ?? '');
  const activeDomain = role.domains.find(d => d.id === activeId) ?? role.domains[0];
  const completeCount = role.domains.filter(d => domainProgress(d, selected).complete).length;
  const total = questionnaireSize(role);
  const goToNextIncomplete = () => {
    const startIndex = role.domains.findIndex(d => d.id === activeDomain.id);
    const ordered = [...role.domains.slice(startIndex + 1), ...role.domains.slice(0, startIndex + 1)];
    const next = ordered.find(d => !domainProgress(d, selected).complete);
    if (next) setActiveId(next.id);
  };
  const activeProgress = domainProgress(activeDomain, selected);
  return <div className="space-y-5 animate-in fade-in duration-300" data-id="element-117" data-mp="22u9jmo3sktm7">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3" data-id="element-118" data-mp="22tqanww4dfkb">
        <div data-id="element-119" data-mp="1ytftrp6sh4qo">
          <h2 className="text-xl font-semibold text-slate-900" data-id="element-120" data-mp="1ytzefdntq78e">
            Select Questions
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-121" data-mp="1tsa3rz4ijvku">
            For each domain, choose {PICKS_PER_TYPE} Characteristic and{' '}
            {PICKS_PER_TYPE} Behaviour questions.
          </p>
        </div>
        <div className="text-sm text-slate-600 shrink-0" data-id="element-122" data-mp="1jxi0qfs5wgio">
          <span className="font-semibold text-slate-900" data-id="element-123" data-mp="3j7gf54r5i5uh">
            {selected.length}
          </span>{' '}
          of {total} questions selected
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-5" data-id="element-124" data-mp="1cjqxmcq7xq1c">
        {/* Domain list */}
        <nav aria-label="Competency domains" className="lg:w-64 shrink-0" data-id="element-125" data-mp="1haphfmaf169c">
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2" data-id="element-126" data-mp="1hd29is9w0x04">
            <span data-id="element-127" data-mp="17kwfld02s5ia">Domains</span>
            <span data-id="element-128" data-mp="17lxuxto4je5t">
              {completeCount} of {role.domains.length} complete
            </span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-1.5 mb-3" data-id="element-129" data-mp="1vba4iund5o8n">
            <div className="bg-slate-900 h-1.5 rounded-full transition-all duration-300" style={{
            width: `${completeCount / role.domains.length * 100}%`
          }} data-id="element-130" data-mp="208sbifhtlte0" />
          </div>
          <ul className="space-y-1 max-h-[480px] overflow-y-auto pr-1" data-id="element-131" data-mp="1qdia9tz8xq1a">
            {role.domains.map(domain => {
            const p = domainProgress(domain, selected);
            const isActive = domain.id === activeDomain.id;
            return <li key={domain.id} data-id="element-132" data-mp="7boefglzpvkv">
                  <button type="button" onClick={() => setActiveId(domain.id)} aria-current={isActive ? 'true' : undefined} className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'}`} data-id="element-133" data-mp="7esq0z1sbtlk">
                    {p.complete ? <CheckCircle2 className={`h-4 w-4 shrink-0 ${isActive ? 'text-emerald-300' : 'text-emerald-600'}`} data-id="element-134" data-mp="3ftklqhpat47p" /> : <span className={`h-4 w-4 shrink-0 rounded-full border ${isActive ? 'border-slate-500' : 'border-slate-300'}`} data-id="element-135" data-mp="3pqp7igf8exnz" />}
                    <span className="flex-1 min-w-0 truncate" data-id="element-136" data-mp="2x333x8607t14">
                      {domain.name}
                    </span>
                    <span className={`text-xs shrink-0 ${isActive ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-137" data-mp="20chi2i8wixtk">
                      {p.total}/{PICKS_PER_DOMAIN}
                    </span>
                  </button>
                </li>;
          })}
          </ul>
        </nav>

        {/* Active domain */}
        <section className="flex-1 min-w-0" aria-labelledby="active-domain" data-id="element-138" data-mp="2j1xm3fjsp8eq">
          <div className="flex items-center justify-between gap-3 mb-4" data-id="element-139" data-mp="2o0h8fh2awm7m">
            <h3 id="active-domain" className="text-base font-semibold text-slate-900" data-id="element-140" data-mp="2nyo2k3zii60o">
              {activeDomain.name}
            </h3>
            {completeCount < role.domains.length && <button type="button" onClick={goToNextIncomplete} disabled={!activeProgress.complete} className="text-sm font-medium text-slate-700 hover:text-slate-900 inline-flex items-center disabled:opacity-40 disabled:cursor-not-allowed shrink-0" data-id="element-141" data-mp="xobbo2wiagmd">
                Next incomplete domain
                <ChevronRight className="h-4 w-4 ml-0.5" data-id="element-142" data-mp="qatxrl7s9t9o" />
              </button>}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-id="element-143" data-mp="lbh8yuxcqc4g">
            {(['characteristic', 'behaviour'] as ItemType[]).map(type => <ItemColumn key={type} type={type} items={activeDomain[type]} selected={selected} onToggle={item => onToggle(item, activeDomain)} data-id="element-144" data-mp="1e1g4982535hk" />)}
          </div>
        </section>
      </div>
    </div>;
}
function ItemColumn({
  type,
  items,
  selected,
  onToggle
}: {
  type: ItemType;
  items: FrameworkItem[];
  selected: number[];
  onToggle: (item: FrameworkItem) => void;
}) {
  const count = items.filter(i => selected.includes(i.no)).length;
  const full = count >= PICKS_PER_TYPE;
  return <div className="border border-slate-200 rounded-xl p-4 bg-white" data-id="element-145" data-mp="3mbfk0t7ho660">
      <div className="flex items-center justify-between mb-3" data-id="element-146" data-mp="3r9qun65lykpa">
        <h4 className="text-sm font-semibold text-slate-900" data-id="element-147" data-mp="3r6uy9m9es42y">
          {ITEM_TYPE_LABEL[type]}
        </h4>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${full ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} aria-live="polite" data-id="element-148" data-mp="3hb1crddpp091">
          {count} of {PICKS_PER_TYPE}
        </span>
      </div>
      <ul className="space-y-2" data-id="element-149" data-mp="38bwco2mi4yur">
        {items.map(item => {
        const isSelected = selected.includes(item.no);
        const blocked = full && !isSelected;
        return <li key={item.no} data-id="element-150" data-mp="2ylbkoy1c2skt">
              <button type="button" onClick={() => onToggle(item)} disabled={blocked} aria-pressed={isSelected} className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${isSelected ? 'border-slate-900 bg-slate-50' : blocked ? 'border-slate-200 opacity-45 cursor-not-allowed' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-151" data-mp="2yifhau6pvboz">
                <span className={`w-4 h-4 mt-0.5 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-slate-900 border-slate-900' : 'border-slate-300 bg-white'}`} data-id="element-152" data-mp="2ezji3hanwj93">
                  {isSelected && <Check className="h-3 w-3 text-white" data-id="element-153" data-mp="2oul6gax45ham" />}
                </span>
                <span className="min-w-0" data-id="element-154" data-mp="2jvqmv6rbnopg">
                  <span className="block text-xs font-medium text-slate-500" data-id="element-155" data-mp="2xprb36kndtbk">
                    {item.competency}
                  </span>
                  <span className="block text-sm text-slate-900 mt-0.5 leading-snug" data-id="element-156" data-mp="2to64g2ypib9t">
                    {item.criterion}
                  </span>
                </span>
              </button>
            </li>;
      })}
      </ul>
      {full && <p className="text-xs text-slate-400 mt-3" data-id="element-157" data-mp="dvx9nef4xzzp">
          Deselect a question to choose a different one.
        </p>}
    </div>;
}